import { css } from '@emotion/react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { cloneElement } from 'react';

import { merge } from 'lib/object';

import {
  skipRegexTokensFromStr,
  toNormalizedASCIIStr,
} from '../../utils/string';
import Badge from '../Badge';
import Text from '../Text';
import { useAutoComplete } from './Context/AutoCompleteProvider';
import TextMatch from './TextMatch';

const StyledAutoCompleteOption = styled('li')`
  ${({ theme, active, isSponsored }) => css`
    align-items: center;
    background: ${active && theme.colors.autoComplete.itemHover};
    cursor: pointer;
    display: flex;
    padding: ${theme.space[2]} ${theme.space[4]};
    justify-content: ${isSponsored ? 'space-between' : 'unset'};
    user-select: none;
  `}
`;

const StyledText = styled(Text)`
  width: 100%;
`;

const splitMatchedFromEntireStr = (str, match) => {
  const normalizedStr = skipRegexTokensFromStr(toNormalizedASCIIStr(str));
  const normalizedMatch = skipRegexTokensFromStr(
    toNormalizedASCIIStr(match.trim())
  );

  const regex = new RegExp(`(${normalizedMatch})`, 'i');
  const matchedInStrIndexStart = normalizedStr.search(regex);

  if (matchedInStrIndexStart < 0) {
    return [str];
  }

  const matchedInStrIndexEnd = matchedInStrIndexStart + normalizedMatch.length;

  const result = [
    str.slice(0, matchedInStrIndexStart),
    str.slice(matchedInStrIndexStart, matchedInStrIndexEnd),
    str.slice(matchedInStrIndexEnd),
  ].filter(Boolean);

  return result.length > 0 ? result : [str];
};

function AutoCompleteOption({
  avatarLeft,
  avatarRight,
  children,
  counter,
  iconLeft,
  iconRight,
  isSponsored,
  value,
  onClick,
  onFocus,
  onMouseOver,
  ...rest
}) {
  const { activeOptionIndex, utils, currInputValue, getOptionIndex } =
    useAutoComplete();

  const { onOptionClick } = utils.getter;

  const onClickHandler = (event) => {
    onOptionClick(value);
    onClick(merge(event, { target: { value } }));
  };

  const splittedChildren =
    currInputValue.getter.length > 0
      ? splitMatchedFromEntireStr(children, currInputValue.getter)
      : [children];

  return (
    <StyledAutoCompleteOption
      active={activeOptionIndex.getter === getOptionIndex(value)}
      isSponsored={isSponsored}
      onClick={onClickHandler}
      onFocus={onFocus}
      onMouseOver={onMouseOver}
      {...rest}
    >
      {iconLeft && cloneElement(iconLeft, { mr: 2 })}
      {avatarLeft && cloneElement(avatarLeft, { size: 'size5', mr: 2 })}
      <StyledText>
        {splittedChildren.map((substr) => (
          <TextMatch
            key={`${value}${substr}`}
            currInputValue={currInputValue.getter}
          >
            {substr}
          </TextMatch>
        ))}
      </StyledText>
      {counter && !isSponsored && (
        <Text as="span" bold={!(iconRight || iconLeft)}>
          {counter}
        </Text>
      )}
      {isSponsored && (
        <Badge type="warning" size="size3" uppercase>
          Patrocinado
        </Badge>
      )}
      {!isSponsored && iconRight && cloneElement(iconRight, { ml: 2 })}
      {avatarRight &&
        !isSponsored &&
        cloneElement(avatarRight, { size: 'size5', mr: 2 })}
    </StyledAutoCompleteOption>
  );
}

AutoCompleteOption.defaultProps = {
  avatarLeft: null,
  avatarRight: null,
  counter: null,
  iconLeft: null,
  iconRight: null,
  isSponsored: false,
  value: '',
  onClick: () => {},
  onFocus: () => {},
  onMouseOver: () => {},
};

AutoCompleteOption.propTypes = {
  /**
   * Renders an avatar on the left side
   */
  avatarLeft: PropTypes.shape(),
  /**
   * Renders an avatar on the right side
   */
  avatarRight: PropTypes.shape(),
  /**
   * Components to be rendered inside component
   */
  children: PropTypes.node.isRequired,
  /**
   * Renders counter alongside the children
   */
  counter: PropTypes.number,
  /**
   * Renders an icon on the left side
   */
  iconLeft: PropTypes.shape(),
  /**
   * Renders an icon on the right side
   */
  iconRight: PropTypes.shape(),
  /**
   * Set the sponsor badge
   */
  isSponsored: PropTypes.bool,
  /**
   * Set the value to be sended
   * - obs: this are a internal prop, and be setup manually
   */
  value: PropTypes.string,
  /**
   * Callback fn to be executed on `onClick` event
   */
  onClick: PropTypes.func,
  /**
   * Callback fn to be executed on `onFocus` event
   */
  onFocus: PropTypes.func,
  /**
   * Callback fn to be executed on `onMouseOver` event
   */
  onMouseOver: PropTypes.func,
};

export default AutoCompleteOption;
